/**
 * Geo base-map allowlist — the security boundary for geographic maps.
 *
 * Charts are rendered from model-generated Vega-Lite specs in the user's
 * browser, so by default any `data.url` is rejected (it would let a spec fetch
 * arbitrary resources). Geographic maps are the one exception: their boundary
 * topology is too large to inline, so the backend serves a curated set of
 * TopoJSON base maps (sourced from S3) at a fixed same-origin route. A spec may
 * load a base map ONLY via that route.
 *
 * This module is the single definition of what counts as an allowed base-map
 * URL, shared by the frontend renderer guard (`@erp/viz`) and the backend
 * `render_chart` validator so the two can never drift. The catalog of which
 * names actually resolve (and their S3 keys) lives backend-only — the browser
 * just needs to know the URL shape is ours.
 */

/** Route prefix under which the backend serves curated TopoJSON base maps. */
export const GEO_BASEMAP_ROUTE = "/api/geo";

/**
 * Strict allowlist pattern for a geo base-map URL a Vega-Lite spec may load.
 *
 * Same-origin, under our geo route, plain filename only. The anchors plus the
 * restricted charset (no `/`, no `.` except the `.json` suffix) are deliberate:
 * they block path-traversal escapes such as `/api/geo/../auth/session`, which a
 * naive prefix check would let through (the browser would normalize it to
 * `/api/auth/session` before fetching).
 */
export const GEO_BASEMAP_URL_PATTERN = /^\/api\/geo\/[a-zA-Z0-9_-]+\.json$/;

/** True when `url` is an allowlisted, same-origin geo base-map URL. */
export function isAllowedGeoBasemapUrl(url: unknown): url is string {
  return typeof url === "string" && GEO_BASEMAP_URL_PATTERN.test(url);
}

/** Build the canonical URL a spec should use to load a base map by name. */
export function geoBasemapUrl(name: string): string {
  return `${GEO_BASEMAP_ROUTE}/${name}.json`;
}
